import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Gift, RefreshCw, TrendingUp } from "lucide-react";
import { api, formatApiError, formatIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { LOYALTY_STATUSES, buildLoyaltySummary, formatLoyaltyRate } from "../lib/loyalty";

export default function BonusWarungPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError("");
        api.get("/users/stats", { signal: controller.signal })
            .then(({ data }) => setStats(data))
            .catch((err) => {
                if (!controller.signal.aborted) setError(formatApiError(err));
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [revision]);

    const summary = buildLoyaltySummary(stats, user);
    const isMember = user?.role === "customer";

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)} aria-label="Kembali" data-testid="bonus-back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">P4Yu Loyalty Program</div>
                        <h1 className="font-heading font-bold text-xl tracking-tight">Bonus Warung</h1>
                    </div>
                </div>
                <Button variant="outline" size="icon" className="rounded-full" onClick={() => setRevision((value) => value + 1)} disabled={loading} aria-label="Segarkan data" data-testid="bonus-refresh-btn">
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
            </div>

            {isMember ? (
                <section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center"><Gift className="h-5 w-5 text-muted-foreground" /></div>
                        <div>
                            <h2 className="font-heading font-bold text-lg">Program khusus Keluarga P4Yu</h2>
                            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Akun Member tetap dapat melakukan seluruh transaksi, tetapi belum mengikuti Bonus Warung dan belum memiliki Status Warung.</p>
                        </div>
                    </div>
                    <Button className="mt-5 bg-brand-green hover:bg-brand-greenDark text-white rounded-xl" onClick={() => navigate("/profil")} data-testid="bonus-upgrade-btn">Upgrade ke Mitra</Button>
                </section>
            ) : (
                <>
                    <section className="rounded-2xl bg-gradient-to-br from-brand-deep to-[#0c2752] text-white p-5 sm:p-7 relative overflow-hidden">
                        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-green/20 blur-3xl" />
                        <div className="relative">
                            <div className="text-xs uppercase tracking-widest text-brand-greenGlow font-semibold">{summary.currentStatus.icon} Status Warung</div>
                            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-2">
                                <div>
                                    <h2 className="font-heading font-extrabold text-3xl">{summary.currentStatus.name}</h2>
                                    <p className="text-white/70 text-sm mt-2">Semakin Ramai Transaksi, Semakin Besar Bonusnya.</p>
                                </div>
                                <div className="sm:text-right">
                                    <div className="text-xs text-white/60">Bonus rate</div>
                                    <div className="font-heading font-extrabold text-2xl text-brand-greenGlow">{formatLoyaltyRate(summary.bonusRate)}</div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {error && <div role="alert" className="rounded-xl border border-destructive/30 p-4 text-sm text-destructive">{error}<Button variant="outline" className="ml-2" onClick={() => setRevision((value) => value + 1)}>Coba lagi</Button></div>}

                    <section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-widest">Omzet bulan berjalan</div>
                                <div className="font-heading font-extrabold text-2xl mt-1">{formatIDR(summary.monthlyTurnover)}</div>
                            </div>
                            <TrendingUp className="h-6 w-6 text-brand-green" />
                        </div>
                        {summary.nextStatus ? (
                            <>
                                <div className="flex justify-between gap-3 text-sm mt-5"><span className="text-muted-foreground">Target berikutnya</span><span className="font-semibold">{summary.nextStatus.icon} {summary.nextStatus.name}</span></div>
                                <div className="h-3 bg-muted rounded-full overflow-hidden mt-3" aria-label={`Progress ${summary.progress}%`}><div className="h-full bg-brand-green rounded-full transition-all" style={{ width: `${summary.progress}%` }} /></div>
                                <div className="flex justify-between gap-3 text-xs text-muted-foreground mt-2"><span>{summary.progress}% tercapai</span><span>Sisa {formatIDR(summary.remaining)}</span></div>
                            </>
                        ) : (
                            <p className="text-sm text-brand-green font-semibold mt-4">Status tertinggi tercapai. Pertahankan transaksi konsisten Anda.</p>
                        )}
                    </section>

                    <section className="rounded-2xl border border-border bg-card overflow-hidden">
                        <div className="p-5 border-b border-border"><h2 className="font-heading font-bold text-lg">Status Warung</h2><p className="text-sm text-muted-foreground mt-1">Target omzet bulanan dan bonus sesuai level Anda.</p></div>
                        <div className="divide-y divide-border">
                            {LOYALTY_STATUSES.map((status, index) => (
                                <div key={status.name} className={`p-4 flex items-center justify-between gap-3 ${index === LOYALTY_STATUSES.indexOf(summary.currentStatus) ? "bg-brand-green/5" : ""}`}>
                                    <div className="flex items-center gap-3 min-w-0"><span className="text-xl">{status.icon}</span><div className="min-w-0"><div className="font-semibold truncate">{status.name}</div><div className="text-xs text-muted-foreground">Target {formatIDR(status.target)}</div></div></div>
                                    <div className="text-right shrink-0"><div className="font-semibold text-brand-green">{status.bonusLabel}</div>{index === LOYALTY_STATUSES.indexOf(summary.currentStatus) && <div className="text-[10px] text-muted-foreground">Status saat ini</div>}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="grid sm:grid-cols-2 gap-4">
                        <InfoBlock title="Periode perhitungan">Tanggal 1 hingga tanggal terakhir setiap bulan. Progress akan kembali ke Rp0 pada awal periode berikutnya, sementara status yang telah dimiliki tetap dipertahankan.</InfoBlock>
                        <InfoBlock title="Bonus diterima">Bonus dihitung dari omzet bulanan sesuai status yang dimiliki dan otomatis ditambahkan ke saldo P4Yu pada awal bulan berikutnya.</InfoBlock>
                        <InfoBlock title="Kenaikan status">Status naik otomatis saat target omzet bulanan tercapai.</InfoBlock>
                        <InfoBlock title="Penurunan status">Status turun 1 level hanya jika tidak ada transaksi selama 3 bulan berturut-turut, sampai minimum Warung Jagoan.</InfoBlock>
                    </section>

                    <div className="rounded-2xl border border-brand-green/20 bg-brand-green/5 p-5 text-sm leading-relaxed">
                        <div className="flex items-center gap-2 font-heading font-bold"><CheckCircle2 className="h-5 w-5 text-brand-green" />Estimasi bonus periode ini</div>
                        <p className="mt-2 text-muted-foreground">Berdasarkan omzet {formatIDR(summary.monthlyTurnover)} dan rate {formatLoyaltyRate(summary.bonusRate)}, estimasi bonus Anda adalah <strong className="text-foreground">{formatIDR(summary.bonusAmount)}</strong>.</p>
                    </div>
                </>
            )}
        </div>
    );
}

function InfoBlock({ title, children }) {
    return <div className="rounded-2xl border border-border bg-card p-5"><h2 className="font-heading font-semibold">{title}</h2><p className="text-sm text-muted-foreground mt-2 leading-relaxed">{children}</p></div>;
}
